import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { formatNYDate } from '../utils/datetime';
import './Dashboard.css';

const Dashboard = () => {
  const { getDashboardMetrics, getUpcomingCases, getAllCases } = useAppContext();
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState({
    todayCourt: 0,
    openCases: 0,
    thisWeek: 0,
    newCases: 0,
  });

  const [upcomingCases, setUpcomingCases] = useState([]);
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [metricData, upcoming, allCases] = await Promise.all([
          getDashboardMetrics(),
          getUpcomingCases(),
          getAllCases(),
        ]);

        const recent = [...allCases]
          .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
          .slice(0, 10);

        setMetrics(metricData);
        setUpcomingCases(upcoming);
        setRecentCases(recent);
      } catch (err) {
        console.error('❌ Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const getBadgeClass = (prefix, value) =>
    `status-badge ${prefix}-${(value || '').toLowerCase().replace(/\s/g, '-')}`;

  return (
    <div className="container dashboard-page">
      <h2 className="page-title">Law Offices of Lawrence S. Hyman Dashboard</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="dashboard-grid">
            <div
              className="card metric clickable"
              onClick={() => navigate('/search?status=Open&sort=soonest')}
            >
              <span className="label">Open Cases</span>
              <span className="value">{metrics.openCases}</span>
            </div>
            <div
              className="card metric clickable"
              onClick={() => navigate('/search?status=New&sort=soonest')}
            >
              <span className="label">New Cases</span>
              <span className="value">{metrics.newCases}</span>
            </div>
            <div
              className="card metric clickable"
              onClick={() => navigate('/search?filter=thisWeek&sort=soonest')}
            >
              <span className="label">This Week's Court Dates</span>
              <span className="value">{metrics.thisWeek}</span>
            </div>
            <div
              className="card metric clickable"
              onClick={() => navigate('/search?filter=today&sort=soonest')}
            >
              <span className="label">Today's Court Appearances</span>
              <span className="value">{metrics.todayCourt}</span>
            </div>
          </div>

          {/* Recently Accessed */}
          <div className="case-header">
            <h3>Recently Accessed Cases</h3>
            <Link to="/intake">
              <button className="new-case-button">New Case</button>
            </Link>
          </div>
          <table className="recent-cases">
            <thead>
              <tr>
                <th>Name</th>
                <th>Ticket #</th>
                <th>Violation</th>
                <th>Phone #1</th>
                <th>Court Date</th>
                <th>Disposition</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {recentCases.map((c) => (
                <tr 
                  key={c.id} 
                  className="clickable-row"
                  onClick={() => navigate(`/case/${c.id}`)}
                >
                  <td>{`${c.firstName} ${c.lastName}`}</td>
                  <td>{c.appearanceNumber}</td>
                  <td>{c.caseType}</td>
                  <td>{c.phone}</td>
                  <td>{c.courtDateTime ? formatNYDate(c.courtDateTime) : '-'}</td>
                  <td>
                    <span className={getBadgeClass('status', c.status)}>{c.status}</span>
                  </td>
                  <td>
                    <Link 
                      to={`/case/${c.id}`} 
                      className="view-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Upcoming */}
          <div className="case-header">
            <h3>Upcoming Cases</h3>
          </div>
          <table className="recent-cases">
            <thead>
              <tr>
                <th>Name</th>
                <th>Ticket #</th>
                <th>Type</th>
                <th>Phone #1</th>
                <th>Court Date</th>
                <th>Disposition</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {upcomingCases.map((c) => (
                <tr 
                  key={c.id}
                  className="clickable-row"
                  onClick={() => navigate(`/case/${c.id}`)}
                >
                  <td>{`${c.firstName} ${c.lastName}`}</td>
                  <td>{c.appearanceNumber}</td>
                  <td>{c.caseType}</td>
                  <td>{c.phone}</td>
                  <td>{c.courtDateTime ? formatNYDate(c.courtDateTime) : '-'}</td>
                  <td>
                    <span className={getBadgeClass('status', c.status)}>{c.status}</span>
                  </td>
                  <td>
                    <Link 
                      to={`/case/${c.id}`} 
                      className="view-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default Dashboard;
